import type {
    DesignerColor,
    DesignerMode,
    DesignerMockupAsset,
    DesignerProduct,
} from "./types";

const defaultCanvasWidth = 520;
const defaultCanvasHeight = 620;
const fallbackPreviewColor: DesignerColor = {
    key: "__preview__",
    name: "Preview",
    hex: "#D7DEE8",
};

export function buildMockupDataUrl(
    product: DesignerProduct,
    color: DesignerColor,
    mode: DesignerMode,
): string {
    const garment = color.hex;
    const line = mixColor(garment, "#0f172a", 0.24);
    const highlight = mixColor(garment, "#ffffff", 0.28);
    const shadow = mixColor(garment, "#33230f", 0.24);
    const panel = rgba(mixColor(garment, "#ffffff", 0.42), 0.36);
    const panelStroke = rgba(mixColor(garment, "#0f172a", 0.24), 0.46);
    const stage = mode === "front" ? "#f7f1e8" : "#efe4d2";
    const art = product.printArea;

    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 520 620">
            <defs>
                <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="18" stdDeviation="18" flood-color="rgba(44, 28, 13, 0.16)"/>
                </filter>
            </defs>
            <rect width="520" height="620" rx="30" fill="${stage}"/>
            <circle cx="452" cy="86" r="102" fill="${rgba("#ffffff", 0.48)}"/>
            <circle cx="92" cy="548" r="118" fill="${rgba("#c85f34", 0.12)}"/>
            <g filter="url(#shadow)">
                ${product.shape === "hoodie"
                    ? hoodieMarkup(garment, line, highlight, shadow, mode)
                    : teeMarkup(garment, line, highlight, shadow, mode)}
            </g>
            <rect
                x="${art.x}"
                y="${art.y}"
                width="${art.width}"
                height="${art.height}"
                rx="24"
                fill="${panel}"
                stroke="${panelStroke}"
                stroke-dasharray="10 10"
            />
            <text
                x="260"
                y="${art.y + art.height + 28}"
                fill="${rgba("#3e2e1f", 0.7)}"
                font-family="'IBM Plex Mono', monospace"
                font-size="13"
                text-anchor="middle"
            >
                ${product.name.toUpperCase()} / ${mode.toUpperCase()} / PRINT ZONE
            </text>
        </svg>
    `;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

export function resolveCanvasBackground(
    product: DesignerProduct,
    color: DesignerColor | null,
    mode: DesignerMode,
    canvasWidth = defaultCanvasWidth,
    canvasHeight = defaultCanvasHeight,
): {
    src: string;
    options: FabricBackgroundImageOptions;
} {
    const previewColor = resolvePreviewColor(color);
    const photoMockup = color ? resolvePhotoMockup(color, mode) : null;

    if (!photoMockup) {
        return {
            src: buildMockupDataUrl(product, previewColor, mode),
            options: {
                crossOrigin: "anonymous",
                left: 0,
                top: 0,
                originX: "left",
                originY: "top",
            },
        };
    }

    const scale = Math.min(canvasWidth / photoMockup.width, canvasHeight / photoMockup.height);
    const scaledWidth = photoMockup.width * scale;
    const scaledHeight = photoMockup.height * scale;

    return {
        src: photoMockup.src,
        options: {
            crossOrigin: "anonymous",
            left: (canvasWidth - scaledWidth) / 2 + (photoMockup.offsetX ?? 0),
            top: (canvasHeight - scaledHeight) / 2 + (photoMockup.offsetY ?? 0),
            originX: "left",
            originY: "top",
            scaleX: scale,
            scaleY: scale,
        },
    };
}

export function resolveCatalogPreviewUrl(
    product: DesignerProduct,
    color: DesignerColor | null,
    mode: DesignerMode,
): string {
    const previewColor = resolvePreviewColor(color);
    const photoMockup = color ? resolvePhotoMockup(color, mode) : null;

    if (photoMockup) {
        return photoMockup.src;
    }

    return buildMockupDataUrl(product, previewColor, mode);
}

function resolvePreviewColor(color: DesignerColor | null): DesignerColor {
    return color ?? fallbackPreviewColor;
}

function resolvePhotoMockup(
    color: DesignerColor,
    mode: DesignerMode,
): (DesignerMockupAsset & { src: string }) | null {
    const candidate = color.mockups?.[mode] ?? null;

    if (!candidate?.src || candidate.width <= 0 || candidate.height <= 0) {
        return null;
    }

    return {
        ...candidate,
        src: candidate.src,
    };
}

function teeMarkup(
    fill: string,
    line: string,
    highlight: string,
    shadow: string,
    mode: DesignerMode,
): string {
    const frontDetails =
        mode === "front"
            ? `
                <path d="M206 116c18 14 90 14 108 0" fill="none" stroke="${shadow}" stroke-width="8" stroke-linecap="round"/>
                <path d="M217 116c12 9 74 9 86 0" fill="none" stroke="${rgba("#ffffff", 0.46)}" stroke-width="4" stroke-linecap="round"/>
            `
            : `
                <path d="M214 106c14 10 78 10 92 0" fill="none" stroke="${rgba("#ffffff", 0.38)}" stroke-width="5" stroke-linecap="round"/>
                <rect x="234" y="114" width="52" height="12" rx="6" fill="${rgba("#ffffff", 0.22)}"/>
            `;

    return `
        <path d="M154 88 196 58h128l42 30 60 50-34 74-43-22v326H171V190l-43 22-34-74z" fill="${fill}" stroke="${line}" stroke-width="6" stroke-linejoin="round"/>
        <path d="M182 90c16-18 42-30 78-36h0c36 6 62 18 78 36" fill="${highlight}" opacity="0.24"/>
        <path d="M171 190c45 12 133 12 178 0" fill="none" stroke="${rgba("#ffffff", 0.18)}" stroke-width="6"/>
        ${frontDetails}
        <path d="M171 516h178" fill="none" stroke="${rgba("#0f172a", 0.14)}" stroke-width="4"/>
    `;
}

function hoodieMarkup(
    fill: string,
    line: string,
    highlight: string,
    shadow: string,
    mode: DesignerMode,
): string {
    const frontDetails =
        mode === "front"
            ? `
                <path d="M208 112c18 24 86 24 104 0" fill="none" stroke="${shadow}" stroke-width="7" stroke-linecap="round"/>
                <path d="M240 144 230 198M280 144 290 198" fill="none" stroke="${rgba("#f7f1e8", 0.9)}" stroke-width="5" stroke-linecap="round"/>
                <rect x="196" y="404" width="128" height="70" rx="26" fill="${rgba("#ffffff", 0.1)}" stroke="${rgba("#0f172a", 0.12)}" stroke-width="4"/>
            `
            : `
                <path d="M194 110c28 28 104 28 132 0" fill="none" stroke="${shadow}" stroke-width="7" stroke-linecap="round"/>
                <path d="M222 124c18 16 58 16 76 0" fill="none" stroke="${rgba("#ffffff", 0.36)}" stroke-width="4" stroke-linecap="round"/>
            `;

    return `
        <path d="M151 118c8-33 40-58 76-66h66c36 8 68 33 76 66l60 52-30 78-47-24-18 292H186l-18-292-47 24-30-78z" fill="${fill}" stroke="${line}" stroke-width="6" stroke-linejoin="round"/>
        <path d="M213 74c12 28 82 28 94 0" fill="${highlight}" opacity="0.25"/>
        ${frontDetails}
        <path d="M186 516h148" fill="none" stroke="${rgba("#0f172a", 0.14)}" stroke-width="4"/>
    `;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
    const normalized = hex.replace("#", "");
    const chunk =
        normalized.length === 3
            ? normalized
                  .split("")
                  .map((char) => char + char)
                  .join("")
            : normalized;

    const number = Number.parseInt(chunk, 16);

    return {
        r: (number >> 16) & 255,
        g: (number >> 8) & 255,
        b: number & 255,
    };
}

function rgbToHex(color: { r: number; g: number; b: number }): string {
    return `#${[color.r, color.g, color.b]
        .map((value) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, "0"))
        .join("")}`;
}

function mixColor(sourceHex: string, targetHex: string, amount: number): string {
    const source = hexToRgb(sourceHex);
    const target = hexToRgb(targetHex);

    return rgbToHex({
        r: source.r + (target.r - source.r) * amount,
        g: source.g + (target.g - source.g) * amount,
        b: source.b + (target.b - source.b) * amount,
    });
}

function rgba(hex: string, alpha: number): string {
    const color = hexToRgb(hex);
    return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
}
